// @ts-check

/**
 * @file App-level Velocious `offlineSync` transport provider and its online
 * drain. The framework queues frontend-model `save()` calls into
 * `frontendModelTransportConfig.offlineSync.mutationLog` only when a provider
 * is installed AND the model's `resourceConfig().sync` is enabled (STC-12/13
 * backend declarations). This module owns the provider wiring, binds its
 * mutation log to the active per-project replica's durable app queue, supplies
 * the acquired offline grant, and drains pending records to the Velocious sync
 * replay endpoint in dependency order.
 */

import FrontendModelBase from "velocious/build/src/frontend-models/base.js"
import SyncApiClient from "velocious/build/src/sync/sync-api-client.js"
import Configuration from "velocious/build/src/configuration.js"

import {syncUuid} from "../database/project-replica-mutation-log.js"
import {projectSyncDeviceId} from "./project-sync-identity.js"
import {acquireOfflineGrant} from "./offline-grant-store.js"

/** @typedef {import("./offline-grant-store.js").FetchGrant} FetchGrant */
/** @typedef {import("./offline-grant-store.js").LoadStoredGrant} LoadStoredGrant */
/** @typedef {import("./offline-grant-store.js").StoreGrant} StoreGrant */

/** @typedef {import("velocious/build/src/sync/local-mutation-log.js").default} LocalMutationLog */
/** @typedef {import("velocious/build/src/sync/sync-api-client-types.js").SyncReplayItem} SyncReplayItem */
/** @typedef {import("velocious/build/src/sync/sync-api-client-types.js").SyncReplayResponse} SyncReplayResponse */
/** @typedef {{record: unknown, result: SyncReplayItem}} ConflictApplyArgs */
/** @typedef {(args: ConflictApplyArgs) => Promise<void>} ConflictApply */

const OFFLINE_REPLAY_PATH = "/velocious/sync/replay"

let activeLogRef = null
let providerObject = null
let armed = false
let grantId = ""
let actorDeviceId = ""
let actorUserId = ""
let installed = false
/** @type {Set<() => void>} */
const providerStateListeners = new Set()

/**
 * Notifies queue-badge consumers that the provider arm state changed.
 * @returns {void} Nothing.
 */
function notifyOfflineSyncProviderStateChange() {
  for (const listener of providerStateListeners) listener()
}

/**
 * Subscribes to offline mutation-queue provider arm-state changes.
 * @param {() => void} listener Callback notified after every enable/disable.
 * @returns {() => void} Idempotent unsubscribe.
 */
export function subscribeOfflineSyncProviderState(listener) {
  providerStateListeners.add(listener)

  return () => { providerStateListeners.delete(listener) }
}

/**
 * Installs the app-level `offlineSync` provider into the Velocious frontend
 * transport exactly once. Until a replica is bound and a grant is acquired the
 * provider reports `enabled: false`, so the framework's offline queue guard
 * never arms and nothing is queued.
 * @returns {void} Nothing.
 */
export function installOfflineSyncProvider() {
  if (installed) return
  installed = true

  const offlineSync = {
    actorDeviceId: "",
    actorUserId: "",
    clientMutationId: syncUuid,
    enabled: false,
    mutationLog: null,
    now: () => new Date(),
    offlineGrant: {id: ""}
  }
  providerObject = offlineSync
  FrontendModelBase.configureTransport({offlineSync})
}

/**
 * Binds the active replica's durable app queue and actor identity to the
 * provider, then acquires (or reuses) the offline grant and arms the queue.
 * A build that configures no signing keys is reported as `enabled: false`
 * rather than rejected: the backend fails closed the same way, so an
 * unconfigured build has offline sync off, not a failing replica open.
 * @param {{actorDeviceId?: string, actorUserId: string, mutationLog: LocalMutationLog}} args Active replica binding.
 * @param {{fetch?: FetchGrant, loadStored?: LoadStoredGrant, store?: StoreGrant, force?: boolean, signingKeys?: {current?: boolean, id: string, secret: string}[], now?: () => Date}} [deps] Injectable acquisition seams.
 * @returns {Promise<{enabled: boolean, grantId: string}>} Arm result; `enabled` is false when the build has no signing keys.
 * @rejects {Error} When keys exist but acquisition fails and no usable grant is available.
 */
export async function bindOfflineSyncProvider({actorDeviceId: deviceId, actorUserId: userId, mutationLog}, deps = {}) {
  installOfflineSyncProvider()
  actorDeviceId = deviceId ?? (await projectSyncDeviceId())
  actorUserId = userId
  activeLogRef = mutationLog
  providerRef().mutationLog = mutationLog

  if (armed && grantId) {
    providerRef().actorDeviceId = actorDeviceId
    providerRef().actorUserId = actorUserId
    notifyOfflineSyncProviderStateChange()
    return {enabled: true, grantId}
  }

  const signingKeys = deps.signingKeys ?? offlineGrantSigningKeys()
  if (signingKeys.length < 1) return {enabled: false, grantId: ""}

  const {grant} = await acquireOfflineGrant(
    {deviceId: actorDeviceId, now: deps.now, signingKeys},
    {fetch: deps.fetch, force: deps.force, loadStored: deps.loadStored, store: deps.store}
  )

  grantId = grant.grantId
  providerRef().actorDeviceId = actorDeviceId
  providerRef().actorUserId = actorUserId
  providerRef().offlineGrant.id = grantId
  providerRef().enabled = true
  armed = true
  notifyOfflineSyncProviderStateChange()
  return {enabled: true, grantId}
}

/**
 * Disables the provider (e.g. Sign-out) without dropping the bound identity.
 * @returns {void} Nothing.
 */
export function disableOfflineSyncProvider() {
  armed = false
  grantId = ""
  activeLogRef = null
  if (!providerObject) return
  providerRef().enabled = false
  providerRef().offlineGrant.id = ""
  providerRef().mutationLog = null
  notifyOfflineSyncProviderStateChange()
}

/**
 * Reports whether the provider is currently armed against a specific mutation
 * log (read-only, for replica lifecycle release decisions).
 * @param {LocalMutationLog} mutationLog Log to test against the active binding.
 * @returns {boolean} Whether this exact log is the provider's bound queue.
 */
export function offlineSyncProviderBoundTo(mutationLog) {
  return armed && activeLogRef === mutationLog
}

/**
 * Releases the provider's binding when the replica that armed it retires: the
 * bound mutation log lives on that replica's SQLite handle, so queueing into it
 * after close throws inside unrelated frontend-model saves (catalog cursor
 * writes surfaced as `sync-failed`). When another replica currently owns the
 * binding, this is a no-op and that replica keeps its armed queue.
 * @param {LocalMutationLog} mutationLog Retiring replica's app queue log.
 * @returns {boolean} Whether the binding was released by this call.
 */
export function releaseOfflineSyncProviderBinding(mutationLog) {
  if (!offlineSyncProviderBoundTo(mutationLog)) return false
  disableOfflineSyncProvider()

  return true
}

/**
 * Signs the offline provider out: disables queueing and clears the durable
 * stored grant. Safe to call when no replica is open.
 * @returns {Promise<void>} Completion after the grant row is removed.
 */
export async function signOutOfflineSync() {
  disableOfflineSyncProvider()
  const {clearStoredGrant} = await import("./offline-grant-store.js")
  await clearStoredGrant()
}

/**
 * Clears provider state (test isolation).
 * @returns {void} Nothing.
 */
export function resetOfflineSyncProviderForTests() {
  armed = false
  grantId = ""
  actorDeviceId = ""
  actorUserId = ""
  activeLogRef = null
  installed = false
  providerStateListeners.clear()
}

/**
 * Reports whether the offline mutation queue is currently armed, without
 * throwing when no provider has been installed yet.
 * @returns {boolean} Enabled arm state (`false` when never installed).
 */
export function offlineSyncProviderEnabled() {
  return providerObject ? providerObject.enabled : false
}

/**
 * Reports the provider's current arm state (read-only, for tests/diagnostics).
 * @returns {{actorDeviceId: string, actorUserId: string, enabled: boolean, grantId: string}} Snapshot.
 */
export function offlineSyncProviderState() {
  const ref = providerRef()
  return {actorDeviceId: ref.actorDeviceId, actorUserId: ref.actorUserId, enabled: ref.enabled, grantId: ref.offlineGrant.id}
}

/**
 * Drains pending frontend-model offline mutations to the Velocious sync replay
 * endpoint, in predecessor/dependency order, applying authoritative conflict
 * state and rebasing successors. Online-gated by the caller (coordinator).
 * @param {{postReplay?: (payload: {authenticationToken: string, syncs: Record<string, ReturnType<typeof JSON.parse>>[]}, options?: {signal?: AbortSignal}) => Promise<SyncReplayResponse>, signal?: AbortSignal, batchSize?: number, resourceTypes?: string[], applyConflict?: ConflictApply, mutationLog?: LocalMutationLog, authenticationToken?: string}} [options] Injectable seams.
 * @returns {Promise<number>} Number of pending records resolved in this pass.
 * @rejects {Error} When a transport round-trip fails.
 */
export async function flushOfflineSyncQueue(options = {}) {
  if (!armed) return 0
  const mutationLog = options.mutationLog ?? activeLogRef
  if (!mutationLog) return 0
  const before = (await mutationLog.records()).filter((record) => record.status === "pending").length

  const postReplay = options.postReplay ?? defaultOfflineReplayTransport
  const signal = options.signal ?? new AbortController().signal
  const resourceTypes = options.resourceTypes ?? pendingModelNames({records: await mutationLog.records()})
  const applyConflict = options.applyConflict ?? defaultConflictApply

  for (const resourceType of resourceTypes) {
    await drainResourceType({
      applyConflict,
      authenticationToken: options.authenticationToken,
      batchSize: options.batchSize,
      mutationLog,
      postReplay,
      resourceType,
      signal
    })
  }

  const after = (await mutationLog.records()).filter((record) => record.status === "pending").length
  return Math.max(0, before - after)
}

/**
 * Drains one resource type's pending records through the framework's ordered
 * conflict-tracked replay, which drains to quiescence (ready records only;
 * successors stay blocked behind non-synced predecessors).
 * @param {{applyConflict: ConflictApply, authenticationToken?: string, batchSize?: number, mutationLog: LocalMutationLog, postReplay: (payload: {authenticationToken: string, syncs: Record<string, ReturnType<typeof JSON.parse>>[]}, options?: {signal?: AbortSignal}) => Promise<SyncReplayResponse>, resourceType: string, signal: AbortSignal}} args Drain inputs.
 * @returns {Promise<void>} Completion when no ready intent remains.
 */
async function drainResourceType({applyConflict, authenticationToken, batchSize, mutationLog, postReplay, resourceType, signal}) {
  const conflictTracking = {
    actorDeviceId: actorDeviceId || "unknown-device",
    actorUserId: actorUserId || "unknown-user",
    clientMutationId: syncUuid,
    mutationLog,
    offlineGrantId: grantId || "unacquired",
    policyHash: "awesome-tasks-offline-sync-v2"
  }
  signal.throwIfAborted()
  await SyncApiClient.replayConflictTrackedSyncs({
    applyConflict,
    authenticationToken: authenticationToken ?? (await offlineReplayAuthenticationToken()),
    batchSize,
    conflictTracking,
    postReplay,
    remoteGeneration: () => 0,
    resourceType,
    signal
  })
}

/**
 * Default replay transport: posts one batch to the framework-owned replay
 * endpoint through the authenticated sync transport.
 * @param {{authenticationToken: string, syncs: Record<string, ReturnType<typeof JSON.parse>>[]}} payload Replay batch.
 * @param {{signal?: AbortSignal}} [options] Cancellation.
 * @returns {Promise<SyncReplayResponse>} Validated response.
 */
async function defaultOfflineReplayTransport(payload, options = {}) {
  const {authenticatedSyncPost} = await import("../database/project-replica-transport.js")
  const result = await authenticatedSyncPost(OFFLINE_REPLAY_PATH, {syncs: payload.syncs}, options)
  const json = result.json()
  return /** @type {SyncReplayResponse} */ (json && typeof json === "object" ? json : {status: "success", syncs: []})
}

/**
 * Default conflict applier: the app-level queue has no tenant model replica to
 * apply an authoritative `serverModel` into, so the framework records the
 * conflict (persisting it durably) and the record is left for the tenant-level
 * authoritative apply or manual review.
 * @param {ConflictApplyArgs} _args Conflict payload.
 * @returns {Promise<void>} Completion.
 */
async function defaultConflictApply(_args) {
  // No tenant-bound replica model is reachable from the app-level queue.
}

/**
 * Resolves the session token for the offline replay transport.
 * @returns {Promise<string>} Session token.
 * @rejects {Error} When no session is active.
 */
async function offlineReplayAuthenticationToken() {
  const {getSessionTokenSync} = await import("../shared/session-option.js")
  const token = getSessionTokenSync()
  if (!token) throw new Error("Offline sync flush requires an authenticated session")
  return token
}

/**
 * Collects the distinct model names that currently have pending offline
 * mutations in the bound log — exactly the models a drain must process.
 * @param {{records: {mutation: {model: string}, status: string}[]}} args Log record snapshot.
 * @returns {string[]} Model names with pending records.
 */
function pendingModelNames({records}) {
  return [...new Set(records.filter((record) => record.status === "pending").map((record) => record.mutation.model))]
}

/**
 * Reads the offline grant verification keys from the frontend configuration.
 * @returns {{current?: boolean, id: string, secret: string}[]} Configured signing keys.
 */
function offlineGrantSigningKeys() {
  try {
    // Velocious normalizes `sync` into its private config and exposes it only
    // through getSyncConfiguration(); a direct `config.sync` property read is
    // always undefined and silently disables the provider (CI sync-failed).
    const config = /** @type {{getSyncConfiguration?: () => {offlineGrantSigningKeys?: {current?: boolean, id: string, secret: string}[]}}} */ (Configuration.current())
    return config.getSyncConfiguration?.()?.offlineGrantSigningKeys ?? []
  } catch {
    return []
  }
}

/**
 * Returns the installed provider object (the exact reference the framework's
 * offline queue guard reads), failing closed if installation has not run.
 * @returns {{actorDeviceId: string, actorUserId: string, enabled: boolean, mutationLog: unknown, offlineGrant: {id: string}}} Provider object.
 * @throws {Error} When the provider has not been installed.
 */
function providerRef() {
  if (!providerObject) throw new Error("Offline sync provider is not installed")
  return providerObject
}
